import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Newspaper } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { sanitizeHtml } from '../lib/sanitize';
import { toEmbedSrc } from '../lib/videoEmbed';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  tags: string[] | null;
  post_type: 'article' | 'vlog' | null;
  video_url: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const NovedadesPostPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase || !slug) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    supabase
      .from('blog_posts')
      .select('id,title,slug,content,cover_image_url,author_name,published_at,tags,post_type,video_url')
      // A Novedad notification links by post id (/novedades/<uuid>); the
      // list and dashboard card link by slug.
      .eq(UUID_RE.test(slug) ? 'id' : 'slug', slug)
      .eq('status', 'published')
      .eq('show_in_crm', true)
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data && data.length) setPost(data[0] as BlogPost);
        else setPost(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('es-GT', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
  const isVlog = post?.post_type === 'vlog' && !!post?.video_url;
  const embedSrc = isVlog && post?.video_url ? toEmbedSrc(post.video_url) : null;

  return (
    <div className="animate-page-entry max-w-3xl mx-auto">
      <button onClick={() => navigate('/novedades')} className="flex items-center gap-1.5 text-sm font-semibold text-indigo-500 hover:text-indigo-400 mb-4">
        <ArrowLeft size={16} /> Novedades
      </button>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !post ? (
        <div className={clsx('text-center py-16 rounded-2xl border', isLight ? 'bg-white border-slate-100 text-slate-400' : 'glass-panel border-white/5 text-slate-500')}>
          <Newspaper size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-semibold">Novedad no encontrada</p>
        </div>
      ) : (
        <article className={clsx('rounded-2xl border overflow-hidden', isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5')}>
          {isVlog && embedSrc ? (
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={embedSrc}
                title={post.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : post.cover_image_url ? (
            <div className="aspect-video overflow-hidden">
              <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
            </div>
          ) : null}

          <div className="p-6">
            {post.tags?.length ? (
              <div className="flex gap-1.5 flex-wrap mb-3">
                {post.tags.map((t) => (
                  <span key={t} className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-chip uppercase', isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-300')}>{t}</span>
                ))}
              </div>
            ) : null}
            <h1 className={clsx('text-2xl sm:text-3xl font-black tracking-tight leading-tight', isLight ? 'text-slate-900' : 'text-white')}>{post.title}</h1>
            <p className="text-sm text-slate-500 mt-2">{post.author_name || 'Intercambio Inmobiliario'} · {fmtDate(post.published_at)}</p>

            {post.content?.trim() ? (
              <div
                className={clsx('prose prose-slate max-w-none mt-6', !isLight && 'prose-invert')}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content, { allowImages: true }) }}
              />
            ) : null}
          </div>
        </article>
      )}
    </div>
  );
};
